import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';
import Attachments from '../components/Attachments';

const MySubmissions = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/employee-expenses/mine');
        setExpenses(res.data);
      } catch (e) {
        toast.error('Failed to load your submissions');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(v) || 0);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN') : '');

  const statusBadge = (s) => {
    const map = {
      pending: { background: '#fef3c7', color: '#92400e' },
      approved: { background: '#d1fae5', color: '#065f46' },
      rejected: { background: '#fee2e2', color: '#991b1b' },
    };
    const st = map[s] || map.pending;
    return <span className="badge" style={st}>{s}</span>;
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Submissions</h1>
          <p className="page-subtitle">Track the status of your submitted expenses</p>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {expenses.length === 0 ? (
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <h3 className="empty-state-title">No submissions yet</h3>
              <p className="empty-state-text">Your submitted expenses will appear here</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Expense ID</th>
                    <th>Date</th>
                    <th>Nature</th>
                    <th>Contractor</th>
                    <th>Bill No.</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr key={e._id}>
                      <td style={{ fontWeight: 600 }}>{e.expenseId}</td>
                      <td>{formatDate(e.date)}</td>
                      <td>{e.expenseNature}</td>
                      <td>{e.contractorName}</td>
                      <td>{e.billNumber || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.amount)}</td>
                      <td style={{ textAlign: 'right', color: Number(e.balanceAmount) > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatCurrency(e.balanceAmount)}</td>
                      <td>{statusBadge(e.status)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setSelected(e)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Expense {selected.expenseId}</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.9rem' }}>
                <div><strong>Date:</strong> {formatDate(selected.date)}</div>
                <div><strong>Status:</strong> {statusBadge(selected.status)}</div>
                <div><strong>Nature:</strong> {selected.expenseNature}</div>
                <div><strong>Contractor:</strong> {selected.contractorName}</div>
                <div><strong>Project:</strong> {selected.projectName || '—'}</div>
                <div><strong>Bill No:</strong> {selected.billNumber || '—'}</div>
                <div><strong>Payment Mode:</strong> {selected.paymentMode}</div>
                <div><strong>Paid By:</strong> {selected.paidBy || '—'}</div>
                <div><strong>Amount:</strong> {formatCurrency(selected.amount)}</div>
                <div><strong>Paid:</strong> {formatCurrency(selected.paidAmount)}</div>
                <div><strong>Balance:</strong> {formatCurrency(selected.balanceAmount)}</div>
                <div><strong>Submitted:</strong> {formatDateTime(selected.createdAt)}</div>
              </div>
              {selected.remarks && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label className="form-label">Remarks</label>
                  <div style={{ fontSize: '0.9rem' }}>{selected.remarks}</div>
                </div>
              )}
              {selected.adminRemark && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label className="form-label">Admin Remark</label>
                  <div className="alert" style={{ fontSize: '0.9rem' }}>{selected.adminRemark}</div>
                </div>
              )}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Attachments</label>
                <Attachments attachments={selected.attachments} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubmissions;
